import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEncryptedParams } from "../../hooks/useEncryptedParams";
import apiClient from "../../services/apiClient";
import { normalizeApiError } from "../../utils/apiError";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import DatePicker from "../../components/common/DatePicker";
import SearchableDropdown from "../../components/common/SearchableDropdown";
import ErrorAlert from "../../components/common/ErrorAlert";
import alertService from "../../utils/alertService";
import shippingLineService from "../../services/shippingLineService";
import purchaseMasterService from "../../services/purchaseMasterService";
import { validateFileSize, validateFileType } from "../../utils/validators";
import ShippingLineCreateModal from "../Modals/ShippingLineCreateModal";

const FilePreviewField = ({ label, value, onChange, error, accept = ".pdf" }) => {
  const isFile = value instanceof File;
  const hasValue = Boolean(value);

  return (
    <div className="space-y-1">
      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 block">
        {label}
      </label>
      {hasValue ? (
        <div className="flex items-center gap-2 p-1.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
          <div className="flex-1 truncate pl-1 text-[11px] font-semibold text-slate-600 dark:text-slate-300">
            {isFile ? value.name : "View Uploaded PDF"}
          </div>
          <div className="flex items-center gap-1">
            <a
              href={isFile ? URL.createObjectURL(value) : value}
              target="_blank"
              rel="noopener noreferrer"
              className="h-8 w-8 flex items-center justify-center rounded-lg bg-white dark:bg-slate-700 text-primary-600 shadow-sm hover:text-primary-700 transition-all border border-slate-100 dark:border-slate-600"
              title="View PDF"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
            </a>
            <button
              type="button"
              onClick={() => onChange(null)}
              className="h-8 w-8 flex items-center justify-center rounded-lg bg-white dark:bg-slate-700 text-rose-500 shadow-sm hover:text-rose-600 transition-all border border-slate-100 dark:border-slate-600"
              title="Clear selection"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      ) : (
        <div className="relative group">
          <input
            type="file"
            accept={accept}
            onChange={(e) => onChange(e.target.files[0])}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
          />
          <div className="flex items-center gap-3 px-3 py-1.5 rounded-lg border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 group-hover:border-primary-500 transition-all">
            <div className="h-7 w-7 flex items-center justify-center rounded-md bg-white dark:bg-slate-700 shadow-sm text-slate-400 group-hover:text-primary-500 transition-colors border border-slate-100 dark:border-slate-600">
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
            </div>
            <span className="text-[11px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-tight">
              Upload PDF
            </span>
          </div>
        </div>
      )}
      {error && (
        <p className="text-[10px] font-bold text-rose-500 uppercase tracking-tight mt-1">
          {error}
        </p>
      )}
    </div>
  );
};

function BLFormPage() {
  const navigate = useNavigate();
  const { id } = useEncryptedParams();
  const basePath = "/bl-master";

  const [shippingLineOptions, setShippingLineOptions] = useState([]);
  const [purchaseMasterInfo, setPurchaseMasterInfo] = useState(null);
  const [isShippingLineModalOpen, setIsShippingLineModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    pmId: "",
    shippingLineId: "",
    blNumber: "",
    dateofDeparture: "",
    eta: "",
    etaIcd: "",
    freeDays: "",
    status: "SI Pending",
    blFileUpload: null,
    isActive: true,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [errors, setErrors] = useState({});

  const isEditPage = Boolean(id);

  // --- Auto-calculate ETA ICD ---
  useEffect(() => {
    if (formData.eta && formData.freeDays) {
      const etaDate = new Date(formData.eta);
      if (!Number.isNaN(etaDate.getTime())) {
        const resultDate = new Date(etaDate);
        resultDate.setDate(
          resultDate.getDate() + parseInt(formData.freeDays, 10),
        );
        const formatted = resultDate.toISOString().split("T")[0];
        setFormData((prev) => ({ ...prev, etaIcd: formatted }));
      }
    } else if (formData.etaIcd && (!formData.eta || !formData.freeDays)) {
      setFormData((prev) => ({ ...prev, etaIcd: "" }));
    }
  }, [formData.eta, formData.freeDays, formData.etaIcd]);

  const loadOptions = async () => {
    try {
      const lines = await shippingLineService.getActiveShippingLines();
      const items = lines?.items || lines?.data || lines || [];
      setShippingLineOptions(
        items.map((l) => ({ label: l.shippingLineName, value: l.id })),
      );
    } catch (err) {
      console.error("Failed to load shipping lines", err);
    }
  };

  useEffect(() => {
    loadOptions();
  }, []);

  useEffect(() => {
    if (!id) return;
    let isMounted = true;
    const loadData = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient.get(`/bl-master/${id}`);
        const data = response?.data?.data || response?.data || {};
        if (isMounted) {
          setFormData({
            pmId: data.pmId || "",
            shippingLineId: data.shippingLineId || "",
            blNumber: data.blNumber || "",
            dateofDeparture: data.dateofDeparture
              ? data.dateofDeparture.split("T")[0]
              : "",
            eta: data.eta ? data.eta.split("T")[0] : "",
            etaIcd: data.etaIcd ? data.etaIcd.split("T")[0] : "",
            freeDays: data.freeDays || "",
            status: data.status || "SI Pending",
            blFileUpload: data.blFileUploadUrl || null,
            isActive: data.isActive ?? true,
          });

          if (data.pmId) {
            const pm = await purchaseMasterService.getPurchaseMasterById(
              data.pmId,
            );
            setPurchaseMasterInfo(pm);
          }
        }
      } catch (err) {
        setFormError(normalizeApiError(err).message);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    loadData();
    return () => {
      isMounted = false;
    };
  }, [id]);

  const handleSave = async () => {
    setFormError(null);
    setErrors({});

    // --- Mandatory Validation ---
    const newErrors = {};
    if (!formData.blNumber?.trim()) newErrors.blNumber = "BL Number is required";
    if (!formData.shippingLineId)
      newErrors.shippingLineId = "Shipping Line is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return alertService.error("Please fill all mandatory fields.");
    }

    // --- Date Validation ---
    if (purchaseMasterInfo?.billDate && formData.dateofDeparture) {
      if (
        formData.dateofDeparture < purchaseMasterInfo.billDate.split("T")[0]
      ) {
        return alertService.error(
          "Departure Date cannot be earlier than Bill Date.",
        );
      }
    }
    if (formData.dateofDeparture && formData.eta) {
      if (formData.eta < formData.dateofDeparture) {
        return alertService.error(
          "ETA (Arrival) cannot be earlier than Departure Date.",
        );
      }
    }

    if (isEditPage) {
      const result = await alertService.editConfirm(
        "Are you sure you want to update this BL Master?",
      );
      if (!result.isConfirmed) return;
    }

    setIsSubmitting(true);
    try {
      const payload = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === "blFileUpload") {
          if (value instanceof File) payload.append(key, value);
        } else if (value !== null && value !== undefined && value !== "") {
          payload.append(key, value);
        }
      });

      if (isEditPage) {
        await apiClient.patch(`/bl-master/${id}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await apiClient.post("/bl-master", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      alertService.success("BL Master Updated Successfully");
      navigate(basePath);
    } catch (err) {
      const normalized = normalizeApiError(err);
      setFormError(normalized.message);
      alertService.error(normalized.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const labelClass =
    "text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1 block";

  if (isLoading)
    return (
      <div className="p-10 text-center text-slate-500">Loading details...</div>
    );

  return (
    <div className="space-y-8 pb-10">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-black text-slate-800 dark:text-white">
          {isEditPage ? "Edit BL Master" : "Create BL Master"}
        </h1>
        <Button variant="secondary" onClick={() => navigate(basePath)}>
          Back to List
        </Button>
      </div>

      {formError && <ErrorAlert message={formError} />}

      {purchaseMasterInfo && (
        <div className="rounded-2xl border border-primary-100 bg-primary-50/30 p-4 dark:border-primary-900/20 dark:bg-primary-900/10">
          <p className="text-[10px] font-black uppercase tracking-widest text-primary-600 mb-2">
            Linked Purchase Details
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-[10px] text-slate-500 uppercase">Bill No</p>
              <p className="text-sm font-bold">{purchaseMasterInfo.billNo}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-500 uppercase">Booking No</p>
              <p className="text-sm font-bold">
                {purchaseMasterInfo.bookingNo}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-slate-500 uppercase">Vendor</p>
              <p className="text-sm font-bold">
                {purchaseMasterInfo.vendorName}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-slate-500 uppercase">Net Weight</p>
              <p className="text-sm font-bold">
                {Number(purchaseMasterInfo.netWeight).toLocaleString()} KG
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Input
            label={
              <>
                BL Number <span className="text-rose-500">*</span>
              </>
            }
            value={formData.blNumber}
            error={errors.blNumber}
            onChange={(e) =>
              setFormData({ ...formData, blNumber: e.target.value })
            }
            labelClassName={labelClass}
            maxLength={50}
          />
          <SearchableDropdown
            label={
              <>
                Shipping Line <span className="text-rose-500">*</span>
              </>
            }
            value={formData.shippingLineId}
            options={shippingLineOptions}
            error={errors.shippingLineId}
            onChange={(e) =>
              setFormData({ ...formData, shippingLineId: e.target.value })
            }
            labelClassName={labelClass}
            onAdd={() => setIsShippingLineModalOpen(true)}
          />

          <DatePicker
            label="Departure Date"
            minDate={purchaseMasterInfo?.billDate?.split("T")[0]}
            value={formData.dateofDeparture}
            onChange={(date) =>
              setFormData({ ...formData, dateofDeparture: date })
            }
            labelClassName={labelClass}
          />
          <DatePicker
            label="ETA (Arrival)"
            minDate={formData.dateofDeparture}
            value={formData.eta}
            onChange={(date) => setFormData({ ...formData, eta: date })}
            labelClassName={labelClass}
          />

          <Input
            label="Free Days"
            type="number"
            placeholder="Enter Free Days"
            value={formData.freeDays}
            onChange={(e) =>
              setFormData({ ...formData, freeDays: e.target.value })
            }
            labelClassName={labelClass}
            min={0}
            max={365}
          />
          <DatePicker
            label="ETA ICD"
            disabled
            value={formData.etaIcd}
            labelClassName={labelClass}
          />

          <FilePreviewField
            label="BL File Upload"
            value={formData.blFileUpload}
            error={errors.blFileUpload}
            onChange={(file) => {
              const sizeError = validateFileSize(file);
              const typeError = validateFileType(file, ['application/pdf']);
              const fileError = sizeError || typeError;
              
              setErrors((prev) => ({ ...prev, blFileUpload: fileError }));
              setFormData({ ...formData, blFileUpload: fileError ? null : file });
            }}
          />

          <div className="flex items-center gap-2 pt-6">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) =>
                setFormData({ ...formData, isActive: e.target.checked })
              }
              className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
            />
            <label
              htmlFor="isActive"
              className="text-sm font-semibold text-slate-700 dark:text-slate-300"
            >
              Is Active
            </label>
          </div>
        </div>
      </div>

      <div className="bottom-4 z-10 flex justify-end gap-4 ">
        <Button
          variant="ghost"
          size="lg"
          className="px-4 border border-slate-200 dark:border-slate-700"
          onClick={() => navigate(basePath)}
        >
          Cancel
        </Button>
        <Button
          size="lg"
          variant="primary"
          isLoading={isSubmitting}
          onClick={handleSave}
          className="px-10"
        >
          {isEditPage ? "Update" : "Save"}
        </Button>
      </div>

      <ShippingLineCreateModal
        isOpen={isShippingLineModalOpen}
        onClose={() => setIsShippingLineModalOpen(false)}
        onSuccess={loadOptions}
      />
    </div>
  );
}

export default BLFormPage;
